import { useState } from "react";
import { Send, Trash2, ChevronDown, Search, X } from "lucide-react";
import { formatDate } from "../../utils/formatters";

const NotificationsTab = ({
  notifications,
  regularUsers,
  notifForm,
  setNotifForm,
  showNotifUserDropdown,
  setShowNotifUserDropdown,
  selectedUserForNotif,
  setSelectedUserForNotif,
  onCreateNotification,
  onDeleteNotification
}) => {
  const [userSearchQuery, setUserSearchQuery] = useState("");

  // Filter users for dropdown
  const filteredUsers = regularUsers.filter(user => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query) ||
      user.client_id?.toLowerCase().includes(query) ||
      user.id?.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <h2 className="font-chivo font-bold text-xl text-white mb-6">Отправка уведомлений</h2>
      
      {/* Create Notification Form */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-6 mb-6">
        <h3 className="font-bold text-white mb-4">Новое уведомление</h3>
        <form onSubmit={onCreateNotification} className="space-y-4">
          {/* Recipient Select with Search */}
          <div className="relative">
            <label className="block text-[#A1A1AA] text-sm mb-2">Получатель</label>
            <button
              type="button"
              onClick={() => {
                setShowNotifUserDropdown(!showNotifUserDropdown);
                if (!showNotifUserDropdown) setUserSearchQuery("");
              }}
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white text-left flex justify-between items-center hover:border-[#2E2E2E]/50 transition-colors"
            >
              <span>{selectedUserForNotif ? `${selectedUserForNotif.name} (${selectedUserForNotif.email})` : "Все пользователи"}</span>
              <ChevronDown size={20} className={`text-[#A1A1AA] transition-transform ${showNotifUserDropdown ? "rotate-180" : ""}`} />
            </button>
            {showNotifUserDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded z-10 max-h-80 overflow-hidden shadow-xl">
                {/* Search input */}
                <div className="p-3 border-b border-[#1F1F1F] sticky top-0 bg-[#0A0A0A]">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Поиск по ID, имени или email..."
                      className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-9 pr-8 py-2 text-white text-sm placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    {userSearchQuery && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserSearchQuery("");
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {userSearchQuery && (
                    <div className="text-xs text-[#A1A1AA] mt-2">
                      Найдено: {filteredUsers.length} из {regularUsers.length}
                    </div>
                  )}
                </div>
                {/* Users list */}
                <div className="max-h-60 overflow-y-auto">
                  {/* All users option */}
                  {!userSearchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setNotifForm({ ...notifForm, user_id: "" });
                        setSelectedUserForNotif(null);
                        setShowNotifUserDropdown(false);
                        setUserSearchQuery("");
                      }}
                      className="w-full px-4 py-3 text-left text-[#EDEDED] hover:bg-[#000000] transition-colors font-medium border-b border-[#1F1F1F]/30"
                    >
                      📢 Все пользователи (массовая рассылка)
                    </button>
                  )}
                  {filteredUsers.length === 0 ? (
                    <div className="px-4 py-6 text-center text-[#A1A1AA]">
                      Пользователи не найдены
                    </div>
                  ) : (
                    filteredUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setNotifForm({ ...notifForm, user_id: u.id });
                          setSelectedUserForNotif(u);
                          setShowNotifUserDropdown(false);
                          setUserSearchQuery("");
                        }}
                        className="w-full px-4 py-3 text-left text-white hover:bg-[#000000] transition-colors border-b border-[#1F1F1F]/30 last:border-b-0"
                      >
                        <div className="font-medium">{u.name}</div>
                        <div className="text-[#A1A1AA] text-sm">{u.email}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Заголовок *</label>
            <input
              type="text"
              value={notifForm.title}
              onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })}
              placeholder="Заголовок уведомления"
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-[#A1A1AA] text-sm mb-2">Текст сообщения *</label>
            <textarea
              value={notifForm.message}
              onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
              placeholder="Текст уведомления..."
              rows={4}
              className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none resize-none"
              required
            />
          </div>

          <button
            type="submit"
            className="bg-[#FF8A1E] hover:bg-[#E67A00] text-white font-bold py-3 px-6 rounded flex items-center gap-2 transition-colors"
          >
            <Send size={18} />
            Отправить уведомление
          </button>
        </form>
      </div>

      {/* Notifications List */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F]">
          <h3 className="font-bold text-white">История уведомлений</h3>
        </div>
        <div className="divide-y divide-[#1F1F1F]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-[#A1A1AA]">
              Нет отправленных уведомлений
            </div>
          ) : (
            notifications.map((notif) => (
              <div key={notif.id} className="p-4 hover:bg-[#1A1A1A]/50 transition-colors">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${notif.user_id ? 'bg-blue-500/15 text-blue-500' : 'bg-white/15 text-[#EDEDED]'}`}>
                        {notif.user_id ? 'Личное' : 'Массовое'}
                      </span>
                      <span className="text-[#A1A1AA] text-xs">
                        {formatDate(notif.created_at)}
                      </span>
                    </div>
                    <h4 className="font-semibold text-white">{notif.title}</h4>
                    <p className="text-[#A1A1AA] text-sm mt-1">{notif.message}</p>
                  </div>
                  <button
                    onClick={() => onDeleteNotification(notif.id)}
                    className="text-[#F87171] hover:text-[#F87171]/70 p-2"
                    title="Удалить"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsTab;
